import FormValidator from '../util/FormValidator';
import FormSender from '../util/FormSender';

export default class ContactForm {
  /**
   * Contact form validator
   *
   * @param {HTMLElement} form Form element
   * @property {Boolean} isValid Validation status of the whole form
   * @property {Array} errors Validation errors
   */
  constructor(form) {
    this.form = form;
    this.isValid = false;
    this.errors = [];
    this.initialize(form);
  }

  initialize() {
    this.step = this.form.querySelector('.form-step');
    this.validator = new FormValidator(this.form);
    this.collapseDuration = 500; // Collapse transition duration in ms
    this.scrollDuration = 750; // Scroll tween duration in ms

    /** Bind events */
    this.bindTextareasResizeEvent();
    this.form.querySelector('.form-button[type="submit"]').addEventListener('click', () => {
      /** Scroll to the step container */
      const offset = document.getElementById('head').getBoundingClientRect().height; /** Scroll offset value */
      window.scroll(0, document.getElementById('contact').getBoundingClientRect().top + window.pageYOffset - offset);

      /** Submit the form */
      this.submitForm();

      if (this.validator.errors.length > 0) {
        /** Whole animation transitions delay */
        setTimeout(() => {
          /** Scroll back to step content or to the first invalid element*/
          let anchor = this.step.getBoundingClientRect().top;
          const firstErrorElt = this.step.querySelector('[class*="is-invalid"]');
          if (firstErrorElt !== null) {
            anchor = firstErrorElt.getBoundingClientRect().top - 15;
          }
          window.scroll(0, anchor + window.pageYOffset - offset);
        }, this.collapseDuration * 2);
      }
    });

    /** Remove built-in validation */
    this.form.setAttribute('novalidate', 'true');
  }

  /**
   * Reset the current step height on a textarea vertical resize
   */
  bindTextareasResizeEvent() {
    for (const textarea of this.form.querySelectorAll('textarea')) {
      textarea.setAttribute('data-height', textarea.clientHeight);
      textarea.addEventListener('mouseup', () => {
        if (parseInt(textarea.getAttribute('data-height'), 10) !== textarea.clientHeight) {
          textarea.setAttribute('data-height', textarea.clientHeight);
          window.collapsable.defineElementHeight(this.step);
        }
      });
    }
  }

  /**
   * Submit the form
   */
  submitForm() {
    if (this.validator.validateForm()) {
      /** Check the captcha and send the form */
      this.checkCaptchaAndSend();
    } else {
      /** Expand the current step */
      setTimeout(() => {
        this.step.classList.remove('is-collapsed');
      }, this.collapseDuration);

      console.error(this.validator.errors);
    }
  }

  /**
   * Check Google reCaptcha validity
   */
  checkCaptchaAndSend() {
    const reCaptchaKey = this.form.getAttribute('data-recaptcha-key');
    const requireRecaptcha = this.form.getAttribute('data-require-recaptcha') === '1';

    if (!reCaptchaKey || reCaptchaKey.length === 0) {
      if (requireRecaptcha) {
        this.showCaptchaError();
      } else {
        this.sendForm();
      }
      return;
    }

    if (!window.grecaptcha || typeof window.grecaptcha.execute !== 'function') {
      this.showCaptchaError();
      return;
    }

    window.grecaptcha.ready(() => {
      window.grecaptcha
        .execute(reCaptchaKey, { action: 'contact' })
        .then(token => this.sendForm(token))
        .catch(() => this.showCaptchaError());
    });
  }

  showCaptchaError() {
    const formStatus = this.form.querySelector('.form-status');
    formStatus.innerHTML = this.form.getAttribute('data-txt-error');
    formStatus.setAttribute('data-status', 'error');
    window.collapsable.defineElementHeight(formStatus.closest('.collapsable'));
    setTimeout(() => {
      formStatus.closest('.collapsable').classList.remove('is-collapsed');
    }, 10);
  }

  /**
   * Send the form
   *
   * @param {String} token
   */
  sendForm(token) {
    /** Set the form data */
    const formData = new FormData(this.form);
    if (token !== undefined) {
      formData.append('token', token);
    }

    /** Send the form */
    const sender = new FormSender(formData, this.form.getAttribute('action'));
    sender.addListener('response', data => this.onSenderResponse(data));
    sender.send();

    /** Set the loading status message */
    const formStatus = this.form.querySelector('.form-status');
    formStatus.innerHTML = this.form.getAttribute('data-txt-sending');
    formStatus.setAttribute('data-status', 'sent');

    /** Expand the form status box */
    window.collapsable.defineElementHeight(formStatus.closest('.collapsable'));
    setTimeout(() => {
      formStatus.closest('.collapsable').classList.remove('is-collapsed');
    }, 10);
  }

  /**
   * Process the form sender response
   * @param {Response} response
   * @param {HTMLElement} step
   */
  onSenderResponse(response) {
    const formStatus = this.form.querySelector('.form-status');
    formStatus.closest('.collapsable').classList.add('is-collapsed');

    setTimeout(() => {
      if (response.sent && response.data.sent && undefined === response.error && undefined === response.data.error) {
        /** Set the confirmation message */
        formStatus.innerHTML = this.form.getAttribute('data-txt-sent');
        formStatus.setAttribute('data-status', 'sent');

        /** Destroy the validator */
        this.validator.destroy();
      } else {
        const serverMessage =
          (response.data && response.data.message) ||
          response.message ||
          this.form.getAttribute('data-txt-error');
        formStatus.innerHTML = serverMessage;
        formStatus.setAttribute('data-status', 'error');

        /** Expand the current step */
        setTimeout(() => {
          this.step.classList.remove('is-collapsed');
        }, this.collapseDuration);

        console.error(response.error, response.data.error);
      }
      /** Expand the form status box */
      window.collapsable.defineElementHeight(formStatus.closest('.collapsable'));
      setTimeout(() => {
        formStatus.closest('.collapsable').classList.remove('is-collapsed');
      }, 10);
    }, this.collapseDuration);
  }
}



// WEBPACK FOOTER //
// ./scripts/components/ContactForm.js